<template>
  <div
    class="extra-section"
    :class="{
      'row align-items-center': section.layout !== 'page_builder',
      'page-builder-section': section.layout === 'page_builder',
      'mt-4': !isFirstSection && !hasGalleryWidget,
      'transparent-background': section.transparent_background,
      'hidden-on-mobile': section.hidden_on_mobile,
      'gallery-widget-section': hasGalleryWidget,
    }"
    :style="[sectionStyle, patternVars]"
    :data-pattern="section.background_type === 'pattern' ? section.pattern_type : null"
    data-aos="fade-up"
  >
    <!-- Pattern backgrounds -->
    <template v-if="section.background_type === 'pattern' && section.pattern_type === 'animated-balls'">
      <span v-for="n in 8" :key="`ball-${n}`" class="ball"></span>
    </template>
    <template v-if="section.background_type === 'pattern' && section.pattern_type === 'rain-lines'">
      <div class="lines">
        <div v-for="n in 10" :key="`line-${n}`" class="line"></div>
      </div>
    </template>
    <template v-if="section.background_type === 'pattern' && section.pattern_type === 'gradient-waves'">
      <div class="wave"></div>
      <div class="wave"></div>
      <div class="wave"></div>
    </template>
    <template v-if="section.background_type === 'pattern' && section.pattern_type === 'floating-circles'">
      <div class="floating-circles-pattern"></div>
    </template>

    <!-- Legacy gallery layouts -->
    <div
      v-if="hasGallery"
      :class="imageColumnClass"
      data-aos="fade-right"
    >
      <div v-if="section.layout === 'gallery_grid'" class="section-gallery-grid">
        <div class="row g-2">
          <div
            v-for="(image, imgIndex) in section.gallery!.slice(0, 6)"
            :key="imgIndex"
            :class="getGalleryGridClass(section.gallery!.length, imgIndex)"
          >
            <img
              :src="getSectionImageUrl(image)"
              :alt="`${section.title} ${imgIndex + 1}`"
              class="section-gallery-image img-fluid rounded"
              loading="lazy"
              @click="emit('image-click', getSectionImageUrl(image))"
            />
          </div>
        </div>
      </div>

      <div v-else-if="section.layout === 'gallery_carousel'" class="section-gallery-carousel">
        <div :id="`carousel-${section.id}`" class="carousel slide" data-bs-ride="carousel">
          <div class="carousel-inner">
            <div
              v-for="(image, imgIndex) in section.gallery"
              :key="imgIndex"
              class="carousel-item"
              :class="{ active: imgIndex === 0 }"
            >
              <img
                :src="getSectionImageUrl(image)"
                :alt="`${section.title} ${imgIndex + 1}`"
                class="d-block w-100 rounded section-carousel-image"
                loading="lazy"
              />
            </div>
          </div>
          <button
            class="carousel-control-prev"
            type="button"
            :data-bs-target="`#carousel-${section.id}`"
            data-bs-slide="prev"
          >
            <span class="carousel-control-prev-icon"></span>
          </button>
          <button
            class="carousel-control-next"
            type="button"
            :data-bs-target="`#carousel-${section.id}`"
            data-bs-slide="next"
          >
            <span class="carousel-control-next-icon"></span>
          </button>
        </div>
      </div>

      <div v-else-if="section.layout?.includes('products_')" class="section-products">
        <div class="products-display">
          <div class="row">
            <div
              v-for="product in products"
              :key="product.id"
              class="col-md-6 col-lg-4 mb-4"
            >
              <div class="product-card">
                <div class="product-image">
                  <img
                    :src="resolveProductImage(product)"
                    :alt="product.name || product.title"
                    class="img-fluid rounded"
                    loading="lazy"
                  />
                </div>
                <div class="product-content">
                  <h4 class="product-title">{{ product.name || product.title }}</h4>
                  <p v-if="product.description" class="product-description">{{ product.description }}</p>
                  <div v-if="product.price" class="product-price">{{ product.price }}€</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="section-single-image">
        <img
          :src="getSectionImageUrl(section.gallery![0])"
          :alt="section.title"
          class="section-image img-fluid rounded"
          loading="lazy"
          @click="emit('image-click', getSectionImageUrl(section.gallery![0]))"
        />
      </div>
    </div>

    <!-- Legacy single image -->
    <div
      v-else-if="section.image"
      class="col-lg-6 col-md-6 mb-4 mb-md-0"
      data-aos="fade-right"
    >
      <img
        :src="getSectionImageUrl(section.image)"
        :alt="section.title"
        class="section-image img-fluid rounded"
        loading="lazy"
        @click="emit('image-click', getSectionImageUrl(section.image))"
      />
    </div>

    <!-- Content -->
    <div
      :class="section.layout === 'page_builder' ? 'page-builder-content' : contentColumnClass"
      :data-aos="section.layout === 'page_builder' ? undefined : 'fade-left'"
    >
      <div class="section-content" :class="{ 'gallery-widget-content': hasGalleryWidget }">
        <WidgetRenderer :section="section" :is-first-section="isFirstSection" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ExtraSection } from '@/types/extraSection'
import {
  getSectionStyle,
  getSectionImageUrl,
  getSectionImageColumnClass,
  getSectionContentColumnClass,
  getGalleryGridClass,
  sectionHasGalleryWidget,
} from '@/composables/useExtraSections'
import WidgetRenderer from '@/components/admin/pagebuilder/WidgetRenderer.vue'
import { getImageUrl } from '@/config/env'

interface SectionProduct {
  id: number
  name?: string
  title?: string
  description?: string
  price?: number
  image_url?: string
}

const props = defineProps<{
  section: ExtraSection
  isFirstSection?: boolean
  products?: SectionProduct[]
}>()

const emit = defineEmits<{
  'image-click': [url: string]
}>()

const sectionStyle = computed(() => getSectionStyle(props.section))

const patternVars = computed(() =>
  props.section.background_type === 'pattern'
    ? {
        '--ball-color-1': props.section.pattern_color || '#006D5B',
        '--ball-color-2': props.section.pattern_color2 || '#FF6F61',
      }
    : {},
)

const hasGalleryWidget = computed(() => sectionHasGalleryWidget(props.section))

const hasGallery = computed(
  () =>
    !hasGalleryWidget.value &&
    props.section.layout !== 'page_builder' &&
    Array.isArray(props.section.gallery) &&
    props.section.gallery.length > 0,
)

const imageColumnClass = computed(() => getSectionImageColumnClass(props.section))
const contentColumnClass = computed(() => getSectionContentColumnClass(props.section))
const products = computed(() => props.products || [])

const resolveProductImage = (product: SectionProduct) =>
  getImageUrl(product.image_url || '/backend/api/image.php?path=placeholder.jpg')
</script>

<style scoped>
.section-content.gallery-widget-content {
  padding: 0;
}

.extra-section.gallery-widget-section {
  margin: 0 !important;
}

.page-builder-section {
  display: block;
  width: 100%;
  max-width: 100%;
  margin-left: 0;
  margin-right: 0;
  box-sizing: border-box;
}

.page-builder-content {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 0 0.75rem;
  box-sizing: border-box;
}

@media (max-width: 767.98px) {
  .page-builder-content {
    padding: 0 0.5rem;
  }
}
</style>
